import clsx from 'clsx';
import { CalendarPlus, Stethoscope, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentsButton } from '../components/documents';
import { LeaveDialog, LeaveStatusPill, periodText, signedHours, useExtentText } from '../components/leave';
import { Button, EmptyState, ErrorBox, Loading, Notice, PageHeader, Select, Stat, td, th, useToast } from '../components/ui';
import { d, num } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { useMe } from '../lib/me';
import { AdjustmentList, type AdjustmentView } from './Overtime';

/**
 * Meine Abwesenheiten: leave balance of the year, own requests and sick reports with their documents. Requests wait
 * for the approver of the person (or "Personal"); sick reports count at once and are confirmed by "Personal".
 * Approved absences go to the Outlook calendar when that is switched on.
 */
export function MyAbsencesPage() {
  const { t } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const thisYear = Number(me.today.slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const [dialog, setDialog] = useState<'request' | 'sick' | null>(null);
  // a request the approver sent back: the dialog opens with its dates, to adjust and send again
  const [redo, setRedo] = useState<{ id: string; kind: string; from: string; to: string; note: string | null } | null>(null);
  const q = useQ('leave.mine', { year });
  const invalidate = ['leave.', 'absences.', 'time.', 'kpi.', 'workload.', 'dashboard.'];
  const withdraw = useM('leave.withdraw', { invalidate });
  const undo = useM('leave.undoWithdraw', { invalidate });
  const extent = useExtentText();
  const data = q.data;
  const b = data?.balance;
  const missing = (data?.absences ?? []).filter((a) => a.documentExpected && a.documents.length === 0 && (a.status === 'requested' || a.status === 'approved'));
  const unit = b ? t(`leave.units.${b.unit}`) : '';

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('leave.title')}
        subtitle={t('leave.subtitle')}
        actions={
          data?.canRequest && (
            <div className="flex flex-wrap gap-2">
              <Button icon={<Stethoscope className="size-4" />} onClick={() => setDialog('sick')}>
                {t('leave.sickNote')}
              </Button>
              <Button variant="primary" icon={<CalendarPlus className="size-4" />} onClick={() => setDialog('request')}>
                {t('leave.request')}
              </Button>
            </div>
          )
        }
      />
      <div className="flex items-center gap-3">
        <Select className="w-32" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label={t('common.year')}>
          {[thisYear - 1, thisYear, thisYear + 1].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
        {data?.outlook && <span className="text-xs text-graphite">{t('leave.outlookNote')}</span>}
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {b && (
        <div className="grid grid-cols-2 gap-4 rounded-lg border border-line bg-sheet p-4 md:grid-cols-5">
          <Stat label={t('leave.balance.entitlement', { year })} value={b.entitlement === null ? '-' : `${num(b.entitlement)} ${unit}`} hint={data?.entitlementNote ?? undefined} />
          <Stat
            label={t('leave.balance.carryover')}
            value={`${num(b.carryover)} ${unit}`}
            hint={b.carryover && b.carryoverUntil ? t('leave.balance.carryoverUntil', { date: d(b.carryoverUntil) }) : undefined}
          />
          <Stat label={t('leave.balance.taken')} value={`${num(b.taken)} ${unit}`} />
          <Stat label={t('leave.balance.planned')} value={`${num(b.planned)} ${unit}`} hint={b.requested ? `${t('leave.balance.requested')}: ${num(b.requested)} ${unit}` : undefined} />
          <Stat
            label={t('leave.balance.available')}
            value={b.available === null ? '-' : `${num(b.available)} ${unit}`}
            tone={b.available !== null && b.available < 0 ? 'bad' : 'good'}
            hint={b.availableAfterRequests !== null && b.requested ? `${t('leave.restAfter')}: ${num(b.availableAfterRequests)} ${unit}` : undefined}
          />
        </div>
      )}
      {missing.length > 0 && (
        <Notice tone="bad">
          {t('documents.missingNotice', { count: missing.length })} {missing.map((a) => `${t(`week.absence.${a.kind as 'sick'}`)} ${periodText(a.from, a.to)}`).join(', ')}
        </Notice>
      )}
      {(data?.absences ?? []).some((a) => a.changeRequested) && <Notice tone="warn">{t('leave.changeHint')}</Notice>}
      {b && b.entitlement === null && <Notice tone="neutral">{t('leave.balance.none')}</Notice>}
      {data?.overtime && year === thisYear && <OvertimePanel o={data.overtime} adjustments={data.overtimeAdjustments} />}
      {data && data.overtimeAccount === false && <Notice tone="neutral">{t('leave.noOvertimeAccount')}</Notice>}
      {data && data.absences.length === 0 && <EmptyState>{t('leave.empty')}</EmptyState>}
      {!!data?.absences.length && (
        <div className="scroll-x rounded-lg border border-line bg-sheet">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                <th className={th}>{t('leave.period')}</th>
                <th className={th}>{t('leave.kind')}</th>
                <th className={th}>{t('leave.extent')}</th>
                <th className={th}>{t('leave.state')}</th>
                <th className={th}>{t('documents.column')}</th>
                <th className={th}>{t('leave.note')}</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {data.absences.map((a) => (
                <tr key={a.id} className={clsx('border-b border-line/70', (a.status === 'cancelled' || a.status === 'rejected') && 'text-graphite')}>
                  <td className={clsx(td, 'num whitespace-nowrap')}>{periodText(a.from, a.to)}</td>
                  <td className={td}>{t(`week.absence.${a.kind as 'vacation'}`)}</td>
                  <td className={clsx(td, 'num')}>{extent(a)}</td>
                  <td className={td}>
                    <LeaveStatusPill status={a.status} />
                    {a.decidedBy && a.status !== 'requested' && <div className="mt-0.5 text-xs text-graphite">{t('leave.decidedBy', { name: a.decidedBy })}</div>}
                  </td>
                  <td className={td}>
                    {(a.documents.length > 0 || a.canAddDocument) && (
                      <DocumentsButton
                        absenceId={a.id}
                        count={a.documents.length}
                        expected={a.documentExpected && (a.status === 'requested' || a.status === 'approved')}
                        title={`${t(`week.absence.${a.kind as 'vacation'}`)} ${periodText(a.from, a.to)}`}
                      />
                    )}
                  </td>
                  <td className={clsx(td, 'max-w-sm text-xs')}>
                    {a.note && <div>{a.note}</div>}
                    {a.decisionNote && (
                      <div className={a.status === 'rejected' ? 'text-warn' : 'text-graphite'}>
                        {a.status === 'rejected' ? `${t('leave.changeNote')}: ` : ''}
                        {a.decisionNote}
                      </div>
                    )}
                  </td>
                  <td className={clsx(td, 'text-right')}>
                    {a.status === 'rejected' && data.canRequest && !a.resubmitted && (
                      <Button size="sm" variant="primary" onClick={() => setRedo({ id: a.id, kind: a.kind, from: a.from, to: a.to, note: a.note })}>
                        {t('leave.resubmit')}
                      </Button>
                    )}
                    {a.resubmitted && <span className="text-xs text-graphite">{t('leave.resubmitted')}</span>}
                    {a.canWithdraw && (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Undo2 className="size-3.5" />}
                        loading={withdraw.isPending && withdraw.variables?.id === a.id}
                        onClick={() =>
                          withdraw
                            .mutateAsync({ id: a.id })
                            .then(() => toast.undoable(t('leave.withdrawn'), t('common.undo'), () => undo.mutateAsync({ id: a.id }).catch(toast.error)))
                            .catch(toast.error)
                        }
                      >
                        {a.status === 'requested' ? t('leave.withdraw') : t('leave.cancel')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LeaveDialog
        open={!!dialog || !!redo}
        mode={dialog ?? 'request'}
        initial={redo}
        onClose={() => {
          setDialog(null);
          setRedo(null);
        }}
      />
    </div>
  );
}

/**
 * Extra hours from the bookings, taken as time off in lieu or paid out (payouts and corrections are entered by
 * HR). A guide from the project bookings, not a working time account.
 */
function OvertimePanel({
  o,
  adjustments,
}: {
  o: { since: string; earnedMinutes: number; adjustedMinutes: number; plannedMinutes: number; requestedMinutes: number; availableMinutes: number; availableAfterRequestsMinutes: number };
  adjustments: AdjustmentView[];
}) {
  const { t } = useTranslation();
  return (
    <section className="rounded-lg border border-line bg-sheet p-4">
      <h2 className="mb-3 text-sm font-semibold">{t('leave.overtime.title')}</h2>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <Stat label={t('leave.overtime.earned')} value={`${signedHours(o.earnedMinutes)} h`} />
        <Stat label={t('leave.overtime.adjusted')} value={`${signedHours(o.adjustedMinutes)} h`} />
        <Stat label={t('leave.overtime.planned')} value={`${signedHours(-o.plannedMinutes)} h`} />
        <Stat label={t('leave.overtime.requested')} value={`${signedHours(-o.requestedMinutes)} h`} />
        <Stat
          label={t('leave.overtime.available')}
          value={`${signedHours(o.availableAfterRequestsMinutes)} h`}
          tone={o.availableAfterRequestsMinutes < 0 ? 'bad' : 'good'}
        />
      </div>
      {adjustments.length > 0 && (
        <div className="mt-3">
          <h3 className="mb-1 text-xs font-semibold text-graphite">{t('overtime.history')}</h3>
          <AdjustmentList items={adjustments} />
        </div>
      )}
      <p className="mt-3 text-xs text-graphite">{t('leave.overtime.hint', { since: d(o.since) })}</p>
    </section>
  );
}
